export type PublishPreviewWarning = {
  code: string
  level: 'info' | 'warn' | 'error'
  message: string
  stage?: string
  vin?: string
}

/**
 * One vehicle group (segment) the VIN belongs to. `groupId` is a stable
 * system identifier (used only for grouping/dedupe in the UI), `groupName`
 * is the human-readable label that should be shown to the user.
 */
export type PublishPreviewGroupMember = {
  groupId: string
  groupName: string
  criteria?: PublishPreviewGroupMembershipCriterion[]
}

export type PublishPreviewGroupMembershipCriterion = {
  field: string
  operator?: string
  expectedValue?: unknown
  currentValue?: unknown
  afterPublishValue?: unknown
  currentSource?: string
  afterPublishSource?: string
  currentMatches: boolean
  afterPublishMatches: boolean
}

export type PublishPreviewGroupSimulationStatus =
  | 'changed'
  | 'unchanged'
  | 'error'
  | 'skipped'

export type PublishPreviewVinGroupSimulation = {
  status: PublishPreviewGroupSimulationStatus
  publishedGroups: PublishPreviewGroupMember[]
  draftMergedGroups: PublishPreviewGroupMember[]
  added: PublishPreviewGroupMember[]
  removed: PublishPreviewGroupMember[]
  reason?: string
}

export type PublishPreviewVinResult = {
  vin: string
  published?: {
    listingRules?: {
      decision?: string
      reason?: string
    }
    groups?: PublishPreviewGroupMember[]
  }
  draftMerged?: {
    listingRules?: {
      decision?: string
      reason?: string
    }
    groups?: PublishPreviewGroupMember[]
  }
  delta?: {
    listingRules?: 'changed' | 'unchanged' | 'error'
    fieldMapping?: 'changed' | 'unchanged' | 'error' | 'skipped'
    groups?: PublishPreviewGroupSimulationStatus
  }
  changedFields?: string[]
  fieldMapping?: {
    status: 'changed' | 'unchanged' | 'missing' | 'skipped' | 'errored'
    changedFields: string[]
    missingFields: string[]
    skippedReason?: string
    fieldValues: Record<
      string,
      {
        publishedValue?: unknown
        previewValue?: unknown
        sourceUsed?: string
        sourceVinFound?: boolean
        status: 'changed' | 'unchanged' | 'missing' | 'skipped' | 'errored'
      }
    >
  }
  groups?: PublishPreviewVinGroupSimulation
}

export type PublishPreviewVinPagination = {
  total: number
  pageSize: number
  offset: number
  hasMore: boolean
  truncated: boolean
}

export type PublishPreviewFieldHealth = {
  key: string
  label: string
  populated: number
  missing: number
  coveragePct: number
  impact?: string
  breakdown?: {
    populated: number
    missing: number
    changed: number
    unchanged: number
    skipped: number
  }
  samples?: {
    changedVins: PublishPreviewFieldHealthSample[]
    missingVins: PublishPreviewFieldHealthSample[]
    skippedVins: PublishPreviewFieldHealthSample[]
    unchangedVins: PublishPreviewFieldHealthSample[]
  }
}

export type PublishPreviewFieldHealthSample = {
  vin: string
  publishedValue?: unknown
  previewValue?: unknown
  sourceUsed?: string
  sourceVinFound?: boolean
  status: 'changed' | 'unchanged' | 'missing' | 'skipped' | 'errored'
  reason?: string
}

export type PublishPreviewStage = {
  available: boolean
  executed: boolean
  deferred?: boolean
  diffSummary?: {
    changed: boolean
    details: Record<string, unknown>
  }
}

export type PublishPreviewListingRulesImpact = {
  listedToDelisted: number
  delistedToListed: number
  reasonChanged: number
}

export type PublishPreviewFieldChangeSample = {
  field: string
  count: number
  sampleVins: string[]
}

/**
 * Aggregated per-VIN field-mapping outcomes returned by the backend whenever
 * the simulation has at least one evaluated VIN with a field-mapping result.
 *
 * Surfaced separately from `byStage.fieldMapping` (which carries config-diff
 * metadata) so the UI can render dealer-friendly counts like
 * "1,234 vehicles will see different mapped values".
 */
export type PublishPreviewFieldMappingChanges = {
  vinsChanged: number
  vinsUnchanged: number
  vinsErrored: number
  vinsSkipped: number
  topChangedFields: PublishPreviewFieldChangeSample[]
  topMissingFields?: PublishPreviewFieldChangeSample[]
}

/**
 * Per-group rollup returned by the backend when the Vehicle Groups stage ran.
 */
export type PublishPreviewGroupChangeBreakdown = {
  groupId: string
  groupName: string
  addedCount: number
  removedCount: number
  sampleVinsAdded: string[]
  sampleVinsRemoved: string[]
}

/**
 * Aggregate group/segment membership simulation results across all evaluated
 * VINs. Always present when the `groups` stage executed (even if no VINs
 * changed membership — counts will be 0). Absence of this field on
 * `byStage.groups` means the stage did not run; consumers should fall back to
 * `stages.groups.diffSummary` for an explanation-only view.
 */
export type PublishPreviewGroupsImpact = {
  vinsChanged: number
  vinsAdded: number
  vinsRemoved: number
  vinsUnchanged: number
  vinsErrored: number
  vinsSkipped: number
  byGroup: PublishPreviewGroupChangeBreakdown[]
}

export type PublishPreviewByStage = {
  listingRules: PublishPreviewListingRulesImpact
  fieldMapping?: Record<string, unknown>
  feedFieldRules?: Record<string, unknown>
  groups?: PublishPreviewGroupsImpact
  preProcessor?: Record<string, unknown>
}

export type PublishPreviewImpact = {
  totals: {
    evaluated: number
    changed: number
    unchanged: number
    errored: number
  }
  byStage: PublishPreviewByStage
  topReasons: Array<{ reason: string; count: number; sampleVins: string[] }>
  fieldHealth?: PublishPreviewFieldHealth[]
  fieldMappingChanges?: PublishPreviewFieldMappingChanges
}

export type PublishPreviewResult = {
  simulationId: string
  jazelId: string
  generatedAt: string
  guardrails: {
    vinCap: number
    totalVins: number
    evaluated: number
    sampled: boolean
    durationMs: number
  }
  warnings: PublishPreviewWarning[]
  stages: Record<string, PublishPreviewStage>
  impact: PublishPreviewImpact
  vinResults: PublishPreviewVinResult[]
  vinResultsPagination?: PublishPreviewVinPagination
}

export type PublishPreviewOptions = {
  maxVins?: number
  feeds?: string[]
  stages?: string[]
  sampleStrategy?: 'first'
  vinPageSize?: number
  vinPageOffset?: number
}

/**
 * @deprecated Prefer `startPublishPreview` + polling. The synchronous endpoint
 * cannot complete within API Gateway's 29s window for full inventory and is
 * retained only for backward compatibility / smaller stage subsets.
 */
export const runPublishPreview = async (params: {
  endpoint: string
  jazelId: string
  sessionToken: string
  options?: PublishPreviewOptions
}): Promise<PublishPreviewResult> => {
  const base = params.endpoint.replace(/\/+$/g, '')
  const response = await fetch(`${base}/publish-preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.sessionToken}`,
    },
    body: JSON.stringify({
      jazelId: params.jazelId,
      options: params.options,
    }),
  })

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(
      payload?.message || payload?.error || `Failed to run publish preview (${response.status})`,
    )
  }

  return payload?.data as PublishPreviewResult
}

// =============================================================================
// Async Publish Preview API
// -----------------------------------------------------------------------------
// The browser flow is:
//   1. POST /publish-preview/run            → returns simulationId
//   2. GET  /publish-preview/{simulationId} → poll until status != 'processing'
//   3. (optional) GET /publish-preview/latest?jazelId=... to detect drift
//
// Stale handling: if a job's draftHash differs from the dealer's current
// draftHash at completion the backend returns status='stale'; the UI must
// not treat that as a valid preview for the current changes.
// =============================================================================

export type PublishPreviewJobStatus = 'processing' | 'complete' | 'failed' | 'stale'
export type PublishPreviewStaleReason = 'config' | 'feed'

export type PublishPreviewProgress = {
  stage: string
  processed: number
  total: number
  percent: number
}

export type PublishPreviewStartResponse = {
  simulationId: string
  jazelId: string
  status: PublishPreviewJobStatus
  draftHash: string
  startedAt: string
  reused: boolean
}

export type PublishPreviewProcessingResponse = {
  simulationId: string
  jazelId: string
  status: 'processing'
  draftHash: string
  startedAt: string
  progress?: PublishPreviewProgress
}

export type PublishPreviewTerminalResponse = {
  simulationId: string
  jazelId: string
  status: 'complete' | 'stale' | 'failed'
  draftHash: string
  finalDraftHash?: string
  startedAt: string
  completedAt?: string
  generatedAt?: string
  resultSummary?: Record<string, unknown>
  result?: PublishPreviewResult
  errorMessage?: string
  staleReasons?: PublishPreviewStaleReason[]
}

export type PublishPreviewStatusResponse =
  | PublishPreviewProcessingResponse
  | PublishPreviewTerminalResponse

export type PublishPreviewLatestResponse = {
  jazelId: string
  currentDraftHash: string | null
  latestSimulationId: string | null
  latestStatus: PublishPreviewJobStatus | null
  latestDraftHash: string | null
  finalDraftHash?: string
  staleReasons?: PublishPreviewStaleReason[]
  isPreviewValid: boolean
  generatedAt: string | null
  startedAt: string | null
  completedAt: string | null
  progress?: PublishPreviewProgress
}

const trimBase = (endpoint: string) => endpoint.replace(/\/+$/g, '')

const authHeaders = (sessionToken: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionToken}`,
})

/**
 * Start a Publish Preview job. Returns the simulationId immediately; callers
 * are expected to poll `getPublishPreviewStatus` until the job reaches a
 * terminal state.
 */
export const startPublishPreview = async (params: {
  endpoint: string
  jazelId: string
  sessionToken: string
  options?: PublishPreviewOptions
}): Promise<PublishPreviewStartResponse> => {
  const base = trimBase(params.endpoint)
  const response = await fetch(`${base}/publish-preview/run`, {
    method: 'POST',
    headers: authHeaders(params.sessionToken),
    body: JSON.stringify({
      jazelId: params.jazelId,
      options: params.options,
    }),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(
      payload?.message || payload?.error || `Failed to start publish preview (${response.status})`,
    )
  }
  return payload?.data as PublishPreviewStartResponse
}

/**
 * Poll a Publish Preview job. Returns either an in-flight processing payload
 * or a terminal payload (complete | stale | failed). The full preview result
 * is inlined for terminal states; callers should treat a 404 as a hard error
 * (the simulationId did not exist or expired via TTL).
 */
export const getPublishPreviewStatus = async (params: {
  endpoint: string
  simulationId: string
  sessionToken: string
  vinPageSize?: number
  vinPageOffset?: number
  changedOnly?: boolean
  affectedField?: string
}): Promise<PublishPreviewStatusResponse> => {
  const base = trimBase(params.endpoint)
  const query = new URLSearchParams()
  if (typeof params.vinPageSize === 'number') query.set('vinPageSize', String(params.vinPageSize))
  if (typeof params.vinPageOffset === 'number') {
    query.set('vinPageOffset', String(params.vinPageOffset))
  }
  if (params.changedOnly) query.set('changedOnly', 'true')
  if (params.affectedField) query.set('affectedField', params.affectedField)
  const qs = query.toString()
  const response = await fetch(
    `${base}/publish-preview/${encodeURIComponent(params.simulationId)}${qs ? `?${qs}` : ''}`,
    {
      method: 'GET',
      headers: authHeaders(params.sessionToken),
    },
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        `Failed to fetch publish preview status (${response.status})`,
    )
  }
  return payload?.data as PublishPreviewStatusResponse
}

/**
 * Look up the latest preview job for a dealer plus the dealer's current
 * draftHash so the UI can decide if the previous preview is still valid.
 */
export const getLatestPublishPreview = async (params: {
  endpoint: string
  jazelId: string
  sessionToken: string
}): Promise<PublishPreviewLatestResponse> => {
  const base = trimBase(params.endpoint)
  const response = await fetch(
    `${base}/publish-preview/latest?jazelId=${encodeURIComponent(params.jazelId)}`,
    {
      method: 'GET',
      headers: authHeaders(params.sessionToken),
    },
  )
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        `Failed to fetch latest publish preview (${response.status})`,
    )
  }
  return payload?.data as PublishPreviewLatestResponse
}
